import {
  Box,
  Column,
  Columns,
  EmptyState,
  FlexBox,
  Text,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ReadSearchApiResponse, SearchResultEnriched } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { searchApi } from 'apis/search';
import { ConversationsBulkToolbar } from 'components/conversation/conversationsBulkToolbar/conversationsBulkToolbar';
import { useSelectConversations } from 'utils/conversation/useSelectConversations/useSelectConversations';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import styles from '../../../searchResults.module.scss';
import { useSearchSplitLayout } from '../../../utils/useSearchSplitLayout';
import { SearchResultsEmpty } from '../../empty/searchResultsEmpty';
import { SearchResultsFeedback } from '../../feedback/searchResultsFeedback';
import { SearchResultsPagination } from '../../pagination/searchResultsPagination';
import { SearchResultsSelectAll } from '../../selectAll/searchResultsSelectAll';
import { SearchResultsSort } from '../../sort/searchResultsSort';

import { ConversationPreview } from './components/preview/conversationPreview';
import { ConversationResult } from './components/result/conversationResult';

type ConversationResult = SearchResultEnriched<'conversations'>;

type ConversationsResultsProps = {
  results: readonly ConversationResult[];
  meta?: ReadSearchApiResponse['meta'];
};

/**
 * Represents a component that is used to render the conversations tab of the
 * search results
 */
export const ConversationsResults = ({
  results,
  meta,
}: ConversationsResultsProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const { params } = useAppSelector((state) => state.search);
  const { hasBulkOperations } = useFeatureFlag(['has-bulk-operations']);

  const [previewedResult, setPreviewedResult] =
    useState<ConversationResult | null>(null);

  const { leftColumnSize, rightColumnSize } = useSearchSplitLayout({
    isPreviewing: !!previewedResult,
  });

  const {
    asyncStatus: selectConversationsAsyncStatus,
    eligibleBulkConversationIds,
    isCurrentPageAllConversationsSelected,
    isCurrentPageSomeConversationsSelected,
    selectedConversationIds,
    totalFilteredConversations,
    selectAllCurrentPageConversation,
    selectAllEligibleConversations,
    toggleSelectConversation,
    resetSelection,
  } = useSelectConversations({
    currentPageConversationIds: results.map(({ result }) => result.id) ?? [],
    onFetchConversationIdsBulk: async () => {
      const { data, meta } = await dispatch(
        searchApi.endpoints.readSelectiveSearch.initiate(
          {
            scope: 'conversations',
            query: params.query,
            filters: params.filters,
            isStrict: params.isStrict,
            selectFields: ['id'],
            pageSize: 1000,
          },
          { forceRefetch: true },
        ),
      ).unwrap();

      const csids = data.results.id?.map(Number) ?? [];

      return {
        csids,
        total_conversations: meta?.total_results,
        validations_failed: [],
      };
    },
  });

  // Handles resetting the selection when the search params change. This excludes the page param, as
  // this will change during pagination and we want to preserve the selection when that occurs.
  useUpdateEffect(() => {
    resetSelection();
  }, [params.query, params.filters, params.sortBy, params.isStrict]);

  const handleRowSelect = useCallback(
    ({ result }: ConversationResult) => {
      toggleSelectConversation(result.id);
    },
    [toggleSelectConversation],
  );

  const handleNavigateToConversation = useCallback(() => {
    if (!previewedResult) return;

    ampli.searchConversationPreviewGoToClicked({
      query: params.query || '',
      csid: previewedResult?.result.id.toString() || '',
    });
  }, [params.query, previewedResult]);

  const handleRowClick = useCallback(
    (result: ConversationResult) => {
      if (result.result.id === previewedResult?.result.id) return;

      setPreviewedResult(result);
      ampli.searchConversationPreviewOpened({
        query: params.query || '',
        csid: result.result.id.toString(),
      });
    },
    [params.query, previewedResult, setPreviewedResult],
  );

  const handlePreviewClose = useCallback(() => {
    setPreviewedResult(null);
    ampli.searchConversationPreviewClosed();
  }, [setPreviewedResult]);

  const showBulkToolbar = selectedConversationIds.length > 0;
  const isAllEligibleConversationsSelected =
    selectedConversationIds.length &&
    selectedConversationIds.length === eligibleBulkConversationIds.length;
  const resultsCount = results.length ?? 0;

  if (resultsCount === 0) {
    return <SearchResultsEmpty />;
  }

  return (
    <Columns>
      <Column size={leftColumnSize}>
        <Column.Row slottable borderBottom noMinHeight>
          <Column.Slot placement="start">
            <SearchResultsSelectAll
              totalCount={resultsCount}
              isAllSelected={isCurrentPageAllConversationsSelected}
              isSomeSelected={isCurrentPageSomeConversationsSelected}
              onChange={
                isAllEligibleConversationsSelected
                  ? resetSelection
                  : selectAllCurrentPageConversation
              }
            />
          </Column.Slot>

          <Column.Slot placement="end">
            <div className={styles.toolbars}>
              {hasBulkOperations && (
                <ConversationsBulkToolbar
                  className={classnames(styles.toolbar, styles.bulkActions, {
                    [styles.active]: showBulkToolbar,
                  })}
                  location="search"
                  asyncStatus={selectConversationsAsyncStatus}
                  eligibleBulkConversationIds={eligibleBulkConversationIds}
                  selectedConversationIds={selectedConversationIds}
                  totalFilteredConversations={totalFilteredConversations}
                  onSelectAll={selectAllEligibleConversations}
                />
              )}

              <SearchResultsSort
                className={classnames(styles.toolbar, styles.sort, {
                  [styles.active]: !showBulkToolbar,
                })}
              />
            </div>
          </Column.Slot>
        </Column.Row>

        <Column.Row scrollable noVerticalPadding noHorizontalPadding>
          {results.map((result, index) => {
            const { result: conversation } = result;

            return (
              <ConversationResult
                className={classnames({
                  [styles.rowSelected]:
                    conversation.id === previewedResult?.result.id,
                })}
                key={`${conversation.id}-${index}`}
                result={result}
                isSelectable={hasBulkOperations}
                isSelected={selectedConversationIds.includes(conversation.id)}
                onRowSelect={handleRowSelect}
                onRowClick={handleRowClick}
              />
            );
          })}
        </Column.Row>

        <Column.Row noVerticalPadding noHorizontalPadding borderTop>
          <SearchResultsPagination
            pageCount={meta?.total_pages ?? 1}
            currentPage={meta?.page ?? 1}
          />
        </Column.Row>
      </Column>

      <Column size={rightColumnSize}>
        <Column.Row scrollable noVerticalPadding noHorizontalPadding>
          {previewedResult ? (
            <ConversationPreview
              csid={previewedResult.result.id}
              highlights={previewedResult.highlights}
              onGoToConversation={handleNavigateToConversation}
              onPreviewClose={handlePreviewClose}
            />
          ) : (
            <Box padding="500 0">
              <EmptyState
                title="Select a conversation"
                description={
                  <FlexBox flexDirection="column" gap="300">
                    <Text.Paragraph>
                      Want to see a preview of that message you&apos;re looking
                      for? <br /> Go ahead and click a conversation to see it
                      here.
                    </Text.Paragraph>

                    <SearchResultsFeedback />
                  </FlexBox>
                }
              />
            </Box>
          )}
        </Column.Row>
      </Column>
    </Columns>
  );
};
